import {
    createIntl,
    createIntlCache,
    defineMessages,
    FormatDateOptions,
    FormatNumberOptions,
} from '@formatjs/intl';

// This is optional but highly recommended
// since it prevents memory leak
const cache = createIntlCache();

export type FormatDateValue = string | number | Date;

export type DateFormatStyle = 'long' | 'short' | 'year';

export type NumberFormatStyle =
    | 'currency'
    | 'currency_short'
    | 'short'
    | 'percent';

export type Formatter = {
    date(
        value: FormatDateValue,
        options?:
            | DateFormatStyle
            | (Omit<FormatDateOptions, 'format'> & {
                  format?: DateFormatStyle;
              })
    ): string;
    ordinal(value: number): string;
    number(
        value: number | bigint,
        options?:
            | NumberFormatStyle
            | (Omit<FormatNumberOptions, 'format'> & {
                  format?: NumberFormatStyle;
              })
    ): string;
};

const DEFAULT_LOCALE = 'en-us' as const;

export function createFormatter(locale?: string): Formatter {
    const messages = defineMessages({
        ordinal: {
            id: 'ordinal',
            defaultMessage: `{value, selectordinal,
                one {#st}
                two {#nd}
                few {#rd}
                other {#th}
            }`,
        },
    });

    const intl = createIntl(
        {
            locale: locale ?? DEFAULT_LOCALE,
            messages: {
                ordinal: `{value, selectordinal,
                        one {#st}
                        two {#nd}
                        few {#rd}
                        other {#th}
                    }`,
            },
            formats: {
                date: {
                    short: {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                    },
                    long: {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    },
                    year: {
                        year: 'numeric',
                    },
                },
                number: {
                    currency: {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 2,
                    },
                    currency_short: {
                        style: 'currency',
                        currency: 'USD',
                        notation: 'compact',
                        compactDisplay: 'short',
                    },
                    percent: {
                        style: 'percent',
                        maximumFractionDigits: 1,
                    },
                    short: {
                        notation: 'compact',
                        compactDisplay: 'short',
                    },
                },
            },
        },
        cache
    );

    return {
        date: (value, options) =>
            intl.formatDate(value, {
                format: typeof options === 'string' ? options : options?.format,
                ...(typeof options === 'object' ? options : undefined),
            }),
        ordinal: (value) =>
            intl.formatMessage(messages.ordinal, {
                value: intl.formatNumber(value, {
                    maximumFractionDigits: 0,
                }),
            }),
        number: (value, options) =>
            intl.formatNumber(value, {
                format: typeof options === 'string' ? options : options?.format,
                ...(typeof options === 'object' ? options : undefined),
            }),
    };
}
